/**
 * PourTargetsSection - Manage Pour2Target records for a pour
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  InputAdornment,
  Alert,
  Paper,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  poursAPI,
  type Pour2Target,
  type Pour2TargetCreate,
  type PourLookups,
} from '../../services/pours';
import productsService from '../../services/products';
import { apiErrorText } from '../../utils/apiError';
import { eaFromStrength, resolveCurve, type CurveType } from '../../utils/regressionConversion';
import {
  formatHalfUp,
  knownMaturityMeta,
  maturityShortLabel,
  maturityUnitText,
} from '../../utils/maturityDisplay';

interface PourTargetsSectionProps {
  pourId: string;
  mode: 'create' | 'edit' | 'view';
  lookups: PourLookups;
  /** The pour's calibration — drives EA auto-calculation from strength. */
  calibrationId?: string;
  /** The pour's stored maturity method; names the target unit (only
   *  "Nurse Saul" is TTF in °C·hrs, every other value EA in hrs). When
   *  absent the unit reads "TTF (°C·hrs) or EA (hrs)". */
  maturityMethod?: string | null;
  /** The pour's specified temperature Ts (a number, or a decimal as text).
   *  An equivalent age is named with it ("EA at 23 °C"); 20 °C when not
   *  given, as the maturity engine's default. */
  specifiedTemp?: number | string | null;
  /** The company strength unit (MPa / Psi / N/mm²); strengths show without
   *  a unit when it is not given. */
  strengthUnit?: string | null;
}

interface TargetFormState {
  Purpose: string;
  Target: string;
  Strength: string;
}

const EMPTY_FORM: TargetFormState = {
  Purpose: '',
  Target: '',
  Strength: '',
};

const PourTargetsSection: React.FC<PourTargetsSectionProps> = ({
  pourId,
  mode,
  lookups,
  calibrationId,
  maturityMethod,
  specifiedTemp,
  strengthUnit,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Units for the target columns and fields. An unknown method names both
  // maturity units once (heading / label), not on every value.
  const maturityUnit = maturityUnitText(maturityMethod);
  const knownMeta = knownMaturityMeta(maturityMethod, specifiedTemp);
  const knownMaturityUnit = knownMeta?.unit ?? '';
  // The target names its index: "Target, EA at 20 °C" (an equivalent age
  // always names its specified temperature) or "Target, TTF".
  const targetLabel = knownMeta
    ? `${t('pourTargets.targetLabel', 'Target')}, ${maturityShortLabel(knownMeta)}`
    : t('pourTargets.targetLabel', 'Target');
  // Values arrive as numbers or as decimals sent as text ("32.00"); shown
  // half up to 1 dp.
  const withMaturityUnit = (value: number | string) =>
    `${formatHalfUp(Number(value))}${knownMaturityUnit ? ` ${knownMaturityUnit}` : ''}`;
  const sUnit = strengthUnit?.trim() || '';
  const withStrengthUnit = (value: number | string) =>
    `${formatHalfUp(Number(value))}${sUnit ? ` ${sUnit}` : ''}`;

  const [targets, setTargets] = useState<Pour2Target[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pour calibration regression (chosen curve + A/B), used to auto-calc EA
  // target from strength.
  const [regression, setRegression] = useState<{ curveType: CurveType; a: number | null; b: number | null } | null>(null);
  const hasRegression = regression?.a != null && regression?.b != null && regression.b !== 0;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState<Pour2Target | null>(null);
  const [formState, setFormState] = useState<TargetFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [targetToDelete, setTargetToDelete] = useState<Pour2Target | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isViewMode = mode === 'view';

  const loadTargets = useCallback(async () => {
    if (!pourId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await poursAPI.getPourTargets(pourId);
      setTargets(data);
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to load targets'));
    } finally {
      setLoading(false);
    }
  }, [pourId]);

  useEffect(() => {
    loadTargets();
  }, [loadTargets]);

  // Load the pour calibration's regression A/B for EA auto-calculation.
  useEffect(() => {
    if (!calibrationId) {
      setRegression(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cal = await productsService.getCalibration(calibrationId);
        if (!cancelled) {
          const { curveType, a, b } = resolveCurve(cal);
          setRegression({ curveType, a, b });
        }
      } catch {
        if (!cancelled) setRegression(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [calibrationId]);

  const handleOpenAdd = () => {
    setEditingTarget(null);
    setFormState(EMPTY_FORM);
    setDialogOpen(true);
  };

  const handleOpenEdit = (target: Pour2Target) => {
    setEditingTarget(target);
    setFormState({
      Purpose: target.Purpose || '',
      Target: target.Target != null ? String(target.Target) : '',
      Strength: target.Strength != null ? String(target.Strength) : '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTarget(null);
    setFormState(EMPTY_FORM);
  };

  const handleFormChange = (field: keyof TargetFormState, value: string) => {
    setFormState((prev) => {
      const next = { ...prev, [field]: value };
      // When a calibration regression is available, strength drives the EA target.
      if (field === 'Strength' && hasRegression) {
        const s = parseFloat(value);
        if (value.trim() !== '' && !Number.isNaN(s)) {
          const ea = eaFromStrength(s, regression!.a, regression!.b, regression!.curveType);
          next.Target = ea != null ? String(ea) : next.Target;
        }
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!formState.Purpose.trim()) return;
    if (!formState.Target.trim()) return;

    const payload: Pour2TargetCreate = {
      Purpose: formState.Purpose.trim(),
      Target: parseFloat(formState.Target),
      Strength: formState.Strength.trim()
        ? parseFloat(formState.Strength)
        : undefined,
    };

    setSaving(true);
    setError(null);
    try {
      if (editingTarget) {
        await poursAPI.updatePourTarget(pourId, editingTarget.ID, payload);
      } else {
        await poursAPI.addPourTarget(pourId, payload);
      }
      handleCloseDialog();
      await loadTargets();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to save target'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (target: Pour2Target) => {
    setTargetToDelete(target);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!targetToDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await poursAPI.deletePourTarget(pourId, targetToDelete.ID);
      setDeleteConfirmOpen(false);
      setTargetToDelete(null);
      await loadTargets();
    } catch (err: any) {
      // Shown above the targets, so the confirmation closes. A refusal (the
      // last target of a pour that stops after its final targets) says why.
      setError(apiErrorText(err, 'Failed to delete target'));
      setDeleteConfirmOpen(false);
      setTargetToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setTargetToDelete(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'Purpose',
      headerName: t('pourTargets.purpose', 'Purpose'),
      flex: 1.5,
      minWidth: 150,
    },
    {
      field: 'Target',
      // Unit-free key: the old 'targetChrs' translation hard-codes "C-hrs".
      // "Target, EA at 20 °C (hrs)" / "Target, TTF (°C·hrs)".
      headerName: `${targetLabel} (${maturityUnit})`,
      flex: 1,
      minWidth: 120,
      type: 'number',
      valueFormatter: (value: number | string | null) =>
        value != null ? withMaturityUnit(value) : '-',
    },
    {
      field: 'Strength',
      headerName: sUnit
        ? `${t('pourTargets.strengthLabel', 'Strength')} (${sUnit})`
        : t('pourTargets.strengthLabel', 'Strength'),
      flex: 1,
      minWidth: 130,
      type: 'number',
      valueFormatter: (value: number | string | null) =>
        value != null ? withStrengthUnit(value) : '-',
    },
    ...(!isViewMode
      ? [
          {
            field: 'actions' as const,
            type: 'actions' as const,
            headerName: t('pourTargets.actions', 'Actions'),
            width: 100,
            minWidth: 100,
            getActions: (params: GridRowParams<Pour2Target>) => [
              <GridActionsCellItem
                icon={<EditIcon />}
                label={t('pourTargets.edit', 'Edit')}
                onClick={() => handleOpenEdit(params.row)}
                color="primary"
              />,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label={t('pourTargets.delete', 'Delete')}
                onClick={() => handleDeleteClick(params.row)}
                color="inherit"
              />,
            ],
          },
        ]
      : []),
  ];

  const renderMobileCard = (target: Pour2Target) => (
    <Card key={target.ID} elevation={1}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {target.Purpose}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {targetLabel}: {target.Target != null ? withMaturityUnit(target.Target) : '-'}
            </Typography>
            {target.Strength != null && (
              <Typography variant="body2" color="text.secondary">
                {t('pourTargets.strengthLabel', 'Strength')}: {withStrengthUnit(target.Strength)}
              </Typography>
            )}
          </Box>
          {!isViewMode && (
            <Box>
              <IconButton
                size="small"
                color="primary"
                aria-label={t('pourTargets.edit', 'Edit')}
                onClick={() => handleOpenEdit(target)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                aria-label={t('pourTargets.delete', 'Delete')}
                onClick={() => handleDeleteClick(target)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        {!isViewMode && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            disabled={loading}
          >
            {t('pourTargets.addTarget', 'Add Target')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : targets.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              {t('pourTargets.noTargets', 'No targets defined yet')}
            </Typography>
          ) : (
            targets.map(renderMobileCard)
          )}
        </Box>
      ) : (
        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={targets}
            columns={columns}
            getRowId={(row) => row.ID}
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 5 } },
            }}
            autoHeight
            disableRowSelectionOnClick
            loading={loading}
            sx={{ border: 0 }}
            slotProps={{
              pagination: {
                labelRowsPerPage: 'Rows per page:',
              },
            }}
          />
        </Paper>
      )}

      {/* Add / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingTarget
            ? t('pourTargets.editTarget', 'Edit Target')
            : t('pourTargets.addTarget', 'Add Target')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t('pourTargets.purpose', 'Purpose')}
              value={formState.Purpose}
              onChange={(e) => handleFormChange('Purpose', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label={t('pourTargets.strengthLabel', 'Strength')}
              value={formState.Strength}
              onChange={(e) => handleFormChange('Strength', e.target.value)}
              fullWidth
              type="number"
              InputProps={{
                endAdornment: sUnit ? (
                  <InputAdornment position="end">{sUnit}</InputAdornment>
                ) : undefined,
              }}
              helperText={
                hasRegression
                  ? t('pourTargets.strengthDrivesTarget', 'Target is auto-calculated from strength')
                  : undefined
              }
            />
            <TextField
              label={
                knownMaturityUnit
                  ? targetLabel
                  : `${targetLabel} (${maturityUnit})`
              }
              value={formState.Target}
              onChange={(e) => handleFormChange('Target', e.target.value)}
              fullWidth
              required
              type="number"
              InputProps={{
                readOnly: hasRegression,
                endAdornment: knownMaturityUnit ? (
                  <InputAdornment position="end">{knownMaturityUnit}</InputAdornment>
                ) : undefined,
              }}
              helperText={
                hasRegression
                  ? t('pourTargets.targetAuto', 'Auto-calculated from the pour calibration')
                  : t('pourTargets.targetManual', 'No calibration regression: enter the target manually')
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            {t('pourTargets.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formState.Purpose.trim() || !formState.Target.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {editingTarget
              ? t('pourTargets.save', 'Save')
              : t('pourTargets.add', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('pourTargets.confirmDelete', 'Confirm Delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('pourTargets.confirmDeleteMessage', 'Are you sure you want to delete this target?')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            {t('pourTargets.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="inherit"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {t('pourTargets.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PourTargetsSection;
